import { useEnumMappings, EnumMappings } from "@/contexts/enum-mappings-context";

/**
 * Maps between display values and enum values for device controls
 */

// Default mappings as fallbacks if backend doesn't provide them
const DEFAULT_MAPPINGS = {
  mode: {
    'cool': 1,
    'fan': 2,
    'dry': 3,
    'heat': 4,
    'auto cool': 5,
    'auto heat': 6
  },
  fanSpeed: {
    'auto': 1,
    'low': 2,
    'med': 3,
    'medium': 3, // Alias for 'med'
    'high': 4,
    'ultra low': 5,
    'ultra-low': 5, // Alias for 'ultra low'
    'fan stop': 6,
    'stop': 6 // Alias for 'fan stop'
  },
  status: {
    'on': 1,
    'off': 0
  },
  operationMode: {
    'remote': 1,
    'local': 0
  }
};

/**
 * Converts a display value to its corresponding enum value for sending to the API
 * @param type The type of enum (mode, fanSpeed, etc.)
 * @param displayValue The display value to convert
 * @param enumMappings The enum mappings from context
 * @returns The enum value to send to the API
 */
export function getEnumValueForDisplay(
  type: 'mode' | 'fanSpeed' | 'status' | 'operationMode',
  displayValue: string,
  enumMappings?: EnumMappings
): number {
  // Normalize the display value for consistent comparison
  const normalizedDisplayValue = displayValue.toLowerCase();

  // Handle special cases for fan speed
  let lookupValue = normalizedDisplayValue;
  if (type === 'fanSpeed') {
    if (normalizedDisplayValue === 'medium') lookupValue = 'med';
    if (normalizedDisplayValue === 'ultra-low') lookupValue = 'ultra low';
    if (normalizedDisplayValue === 'stop') lookupValue = 'fan stop';
  }

  // If we have enum mappings from the backend, prioritize them
  if (enumMappings && Object.keys(enumMappings[type]).length > 0) {
    console.log(`Looking up ${type} value "${displayValue}" (normalized: "${normalizedDisplayValue}", lookup: "${lookupValue}") in enum mappings:`, enumMappings[type]);
    // Find the matching key for the display value (case-insensitive)
    // In the enum mappings, the keys are numeric values and values are display strings
    const entry = Object.entries(enumMappings[type])
      .find(([_, value]) =>
        typeof value === 'string' &&
        value.toLowerCase() === normalizedDisplayValue
      );

    if (entry) {
      console.log(`Found enum mapping for ${type} value "${displayValue}": ${entry[0]}`);
      return parseInt(entry[0], 10);
    }

    // Try again with the lookup value if it's different
    if (lookupValue !== normalizedDisplayValue) {
      const lookupEntry = Object.entries(enumMappings[type])
        .find(([_, value]) =>
          typeof value === 'string' &&
          value.toLowerCase() === lookupValue
        );

      if (lookupEntry) {
        console.log(`Found enum mapping for ${type} value "${lookupValue}": ${lookupEntry[0]}`);
        return parseInt(lookupEntry[0], 10);
      }
    }

    // If not found directly, try using numeric values as fallback
    const numericKey = Object.keys(enumMappings[type]).find(key => {
      const numValue = parseInt(key, 10);
      // Check if the key exists in the default mappings for this display value
      const defaultValue = DEFAULT_MAPPINGS[type][lookupValue as keyof typeof DEFAULT_MAPPINGS[typeof type]];
      return defaultValue === numValue;
    });

    if (numericKey) {
      console.log(`Found numeric key for ${type} value "${displayValue}": ${numericKey}`);
      return parseInt(numericKey, 10);
    }
  }

  // Fall back to defaults if no mapping found in backend enums
  // First try with the lookup value
  let defaultValue = DEFAULT_MAPPINGS[type][lookupValue as keyof typeof DEFAULT_MAPPINGS[typeof type]];

  // If not found, try with the original normalized value
  if (defaultValue === undefined && lookupValue !== normalizedDisplayValue) {
    defaultValue = DEFAULT_MAPPINGS[type][normalizedDisplayValue as keyof typeof DEFAULT_MAPPINGS[typeof type]];
  }

  if (defaultValue !== undefined) {
    console.log(`Using default mapping for ${type} value "${displayValue}": ${defaultValue}`);
    return defaultValue;
  }

  // Absolute fallback: return 0 for status/operation modes, 1 for modes/fan speeds
  console.log(`No mapping found for ${type} value "${displayValue}", using fallback value`);
  return ['status', 'operationMode'].includes(type) ? 0 : 1;
}

/**
 * React hook to simplify working with enum values
 */
export function useControlEnumValues() {
  const { enumMappings } = useEnumMappings();

  return {
    /**
     * Convert a display value to its enum value for a specific type
     * @param type The enum type (mode, fanSpeed, status, operationMode)
     * @param displayValue The display value to convert to an enum value
     */
    getValueForDisplay: (type: 'mode' | 'fanSpeed' | 'status' | 'operationMode', displayValue: string) =>
      getEnumValueForDisplay(type, displayValue, enumMappings),

    /**
     * Get the enum value for a mode (cool, fan, dry, heat, auto, etc.)
     */
    getModeValue: (mode: string) =>
      getEnumValueForDisplay('mode', mode, enumMappings),

    /**
     * Get the enum value for a fan speed (auto, low, med, high, ultra low, fan stop, etc.)
     */
    getFanSpeedValue: (fanSpeed: string) =>
      getEnumValueForDisplay('fanSpeed', fanSpeed, enumMappings),

    /**
     * Get the enum value for status (on, off)
     */
    getStatusValue: (status: string) =>
      getEnumValueForDisplay('status', status, enumMappings),

    /**
     * Get the enum value for operation mode (remote, local)
     */
    getOperationModeValue: (opMode: string) =>
      getEnumValueForDisplay('operationMode', opMode, enumMappings)
  };
}